import { Home, Bus, Truck, ChevronsUpDown, X, Settings, LogOut, Navigation, User, CreditCard, Gauge, MapPinned } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "./ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Input } from "./ui/input";
import { useState, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import { shouldHideSubscriptionPurchase } from "../utils/platform";

const navItems = [
  { name: "Map Dashboard", href: "/customer/dashboard", icon: Home },
  { name: "Profile", href: "/customer/profile", icon: User },
  { name: "Add vehicle", href: "/customer/vehicles/add", icon: Truck },
  { name: "Vehicle list", href: "/customer/vehicles", icon: Bus },
  { name: "Subscription", href: "/customer/subscription", icon: CreditCard },
  { name: "Trip History", href: "/customer/map/trips", icon: Navigation },
  { name: "Speed alerts", href: "/customer/speed-alerts", icon: Gauge },
  { name: "Geofences", href: "/customer/geofences", icon: MapPinned },
];

const profileSettingsItem = { name: "Settings", href: "/customer/profile-settings", icon: Settings };

export function UserSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const hideSubscription = shouldHideSubscriptionPurchase();
  const visibleNavItems = useMemo(
    () =>
      hideSubscription
        ? navItems.filter((item) => item.href !== "/customer/subscription")
        : navItems,
    [hideSubscription]
  );

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Logout failed:', error);
      navigate('/', { replace: true });
    }
  };

  const filterMenuItem = (itemName, subItems = []) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();

    if (itemName.toLowerCase().includes(query)) {
      return true;
    }

    if (subItems.length > 0) {
      return subItems.some((subItem) => subItem.toLowerCase().includes(query));
    }

    return false;
  };

  const clearSearch = () => {
    setSearchQuery("");
  };

  return (
    <Sidebar className="">
      <SidebarHeader className="bg-muted/70">
        <div className="flex items-center gap-2 px-2 sm:px-4">
          <div className="flex items-center justify-center">
            <Truck className="size-3 sm:size-4" />
          </div>
          <div className="grid flex-1 text-left text-xs sm:text-sm leading-tight p-3">
            <span className="truncate font-semibold text-sidebar-foreground">TrackYourWheelz</span>
            <span className="truncate text-xs text-sidebar-foreground/70">User Panel</span>
          </div>
        </div>
        
        {/* Search Menu Items */}
        <div className="px-2 sm:px-4 pb-3 pt-2">
          <div className="relative">
            <Input
              type="text"
              placeholder="Search menu..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 pr-8 h-9 bg-muted/50 border-muted-foreground/20 rounded-lg border-1"
            />
            {searchQuery && (
              <button
                onClick={clearSearch}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="size-4" />
              </button>
            )}
          </div>
        </div>
      </SidebarHeader>
      
      <SidebarContent className="bg-muted/70">
        <SidebarGroup>
          <SidebarGroupLabel>Navigation</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {visibleNavItems.map((item) => {
                if (!filterMenuItem(item.name)) return null;
                const itemHrefPath = item.href.split("?")[0];
                const itemSearch = item.href.includes("?") ? item.href.split("?")[1] : "";
                const itemParams = itemSearch ? new URLSearchParams(itemSearch) : null;
                const currentParams = new URLSearchParams(location.search);

                const isActive =
                  location.pathname === itemHrefPath &&
                  (!itemParams || !itemParams.get("half") || currentParams.get("half") === itemParams.get("half"));
                const Icon = item.icon;
                
                return (
                  <SidebarMenuItem key={item.name}>
                    <SidebarMenuButton asChild isActive={isActive}>
                      <Link to={item.href} className="no-underline text-sidebar-foreground hover:text-sidebar-accent-foreground">
                        <Icon className="size-4" />
                        <span>{item.name}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}

              {/* Profile Settings - Last item */}
              {filterMenuItem(profileSettingsItem.name) && (() => {
                const ProfileSettingsIcon = profileSettingsItem.icon;
                return (
                  <SidebarMenuItem>
                    <SidebarMenuButton asChild isActive={location.pathname === profileSettingsItem.href}>
                      <Link to={profileSettingsItem.href} className="no-underline text-sidebar-foreground hover:text-sidebar-accent-foreground">
                        <ProfileSettingsIcon className="size-4" />
                        <span>{profileSettingsItem.name}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })()}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      
      <SidebarFooter className="bg-muted/50 p-0 m-0">
        <SidebarMenu className="p-0 m-0">
          <SidebarMenuItem className="p-0 m-0">
            <DropdownMenu>
              <DropdownMenuTrigger className="flex w-full items-center gap-2 rounded-none px-2 py-1.5 text-sm outline-none border-0 bg-muted/50 transition-all hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus:outline-none focus:ring-0 focus:border-0 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground">
                <Avatar className="h-8 w-8 rounded-lg">
                  <AvatarImage src="https://media.istockphoto.com/id/2151517587/photo/hospital-clinic-patient-man-with-casual-tshirt-medical-hospital-3d-icon-illustration-render.webp?a=1&b=1&s=612x612&w=0&k=20&c=LGDaPN5miVyTIa31A3BUzo7Gm0GE4DbtkBdLylE32Ss=" alt="User" />
                  <AvatarFallback className="rounded-lg bg-primary/10 text-primary text-xs font-medium">
                    TW
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">{user?.name || "Customer"}</span>
                  <span className="truncate text-xs">{user?.email || "customer@example.com"}</span>
                </div>
                <ChevronsUpDown className="ml-auto size-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="min-w-56 w-[--radix-dropdown-menu-trigger-width] rounded-lg"
                side="right"
                align="end"
              >
                <DropdownMenuLabel className="p-0 font-normal">
                  <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                    <Avatar className="h-8 w-8 rounded-lg">
                      <AvatarImage src="https://media.istockphoto.com/id/2151517587/photo/hospital-clinic-patient-man-with-casual-tshirt-medical-hospital-3d-icon-illustration-render.webp?a=1&b=1&s=612x612&w=0&k=20&c=LGDaPN5miVyTIa31A3BUzo7Gm0GE4DbtkBdLylE32Ss=" alt="User" />
                      <AvatarFallback className="rounded-lg bg-primary/10 text-primary text-xs font-medium">
                        TW
                      </AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-semibold">{user?.name || "Customer"}</span>
                      <span className="truncate text-xs capitalize">Role: {user?.role || "Customer"}</span>
                    </div>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  <DropdownMenuItem asChild className="text-foreground hover:text-foreground">
                    <Link to="/customer/profile" className="no-underline text-foreground hover:text-foreground">
                      <User className="mr-2 h-4 w-4" />
                      Profile
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="text-foreground hover:text-foreground">
                    <Link to="/customer/profile-settings" className="no-underline text-foreground hover:text-foreground">
                      <Settings className="mr-2 h-4 w-4" />
                      Settings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </DropdownMenuGroup>
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="mr-2 h-4 w-4" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
